"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Conservation() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const imageWrapRef = useRef<HTMLDivElement | null>(null);
  const [parallaxY, setParallaxY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const node = imageWrapRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const progress = Math.max(0, Math.min(1, 1 - rect.top / windowHeight));
      setParallaxY(progress * 30);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const benefits = ["Expert guidance on habitat preservation", "Direct support for local sea bird colonies", "Educational resources for public advocacy"];
  const stats = [{
    value: "2",
    label: "REFUGES PROTECTED"
  }, {
    value: "100%",
    label: "COMMUNITY FOCUSED"
  }, {
    value: "DAILY",
    label: "WILDLIFE MONITORING"
  }];
  return <section data-section-id="564" id="start-a-conversation-for-conservation" className="relative w-full min-h-[80vh] bg-secondary text-secondary-foreground overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh]">
        {}
        <div ref={imageWrapRef} className="relative w-full h-[50vh] lg:h-auto overflow-hidden">
          <div className="absolute inset-0 will-change-transform" style={{
          transform: `translate3d(0, ${parallaxY}px, 0)`
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/wildlife_refuge_coastal_view.png" alt="Expansive view of Protection Island National Wildlife Refuge coastline" loading="lazy" decoding="async" className="w-full h-full object-cover scale-110" />
            {}
            <div className="absolute inset-0 pointer-events-none" style={{
            background: "linear-gradient(115deg, hsla(160, 61%, 8%, 0.55) 0%, hsla(160, 61%, 8%, 0.15) 45%, hsla(240, 100%, 50%, 0.12) 100%)"
          }} />
          </div>
          {}
          <div className="hidden lg:block absolute top-0 right-0 h-full w-24 pointer-events-none" style={{
          background: "linear-gradient(90deg, hsla(160, 61%, 8%, 0) 0%, hsl(160, 61%, 8%) 100%)"
        }} />
        </div>

        {}
        <div className="relative flex items-center bg-secondary">
          <div ref={contentRef} className={`w-full px-6 sm:px-10 lg:px-16 py-16 lg:py-24 max-w-2xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-secondary-foreground/70">
              <span className="inline-block w-1.5 h-1.5 rounded-lg bg-primary" style={{
              boxShadow: "0 0 12px hsla(240, 100%, 50%, 0.7)"
            }} aria-hidden="true" />
              <span>Advocacy</span>
            </div>

            {}
            <h1 className="font-default font-semibold leading-[1.05] mt-6 text-secondary-foreground" style={{
            fontSize: "clamp(2.5rem, 6vw, 5rem)"
          }}>
              Protect our shores.{" "}
              <em className="not-italic">
                <span className="italic font-normal text-primary">
                  Preserve our future.
                </span>
              </em>
            </h1>

            {}
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-secondary-foreground/75 font-default max-w-xl">
              Friends of the Refuges is dedicated to informing the public on how to protect our wildlife and sea bird refuges. Join us in safeguarding the natural beauty of Protection Island and Dungeness National Wildlife Refuge for generations to come.
            </p>

            {}
            <ul className="mt-10 space-y-4">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className={`flex items-start gap-4 transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"}`} style={{
              transitionDelay: `${150 + i * 100}ms`
            }}>
                  <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-lg border border-primary/40 bg-primary/10">
                    <Check className="w-3 h-3 text-primary" strokeWidth={3} />
                  </span>
                  <span className="text-secondary-foreground/90 text-base sm:text-lg font-default">
                    {benefit}
                  </span>
                </li>)}
            </ul>

            {}
            <div className="mt-12 pt-8 border-t border-secondary-foreground/15">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-4 font-mono text-xs sm:text-sm tracking-[0.15em] uppercase">
                {stats.map((stat, i) => <div key={i} data-index={i} className="flex items-center gap-x-5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-secondary-foreground font-semibold">
                        {stat.value}
                      </span>
                      <span className="text-secondary-foreground/55">
                        {stat.label}
                      </span>
                    </div>
                    {i < stats.length - 1 && <span className="inline-block w-1 h-1 rounded-lg bg-primary" aria-hidden="true" />}
                  </div>)}
              </div>
            </div>

            {}
            <div className="mt-10">
              <Button asChild size="lg" className="group h-12 px-7 font-default text-xs sm:text-sm tracking-[0.2em] uppercase bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg">
                <Link to="/GetInvolvedPage">
                  Get Involved
                  <ArrowDown className="ml-1 w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}